import {Grid, makeStyles, useMediaQuery} from '@material-ui/core';
import {useQuoteField} from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import {FormRowGrid, GridRowProps} from 'Quoting/components/shared/GridRow';
import {ConditionalSection, QuoteFormConditionalSection} from 'Quoting/components/shared/QuoteFormConditionalSection';
import {QuoteFormElement} from 'Quoting/components/shared/QuoteFormElement';
import {FormWrapper} from 'Quoting/components/shared/QuoteFormWrapper';
import {NoOp} from 'Shared/utilities/IOUtilities';
import React from 'react';
import {CheckboxInputGrid} from '../../../../../shared/CheckboxInputGrid';
import {getParentStructurePath} from '../../Common/PropertyPaths';
import {HealthScreeningServices} from '../HIHealthScreeningServices';
import {getPortabilityPaths} from './propertyPaths';

/* Start: Additional Options and Benefits */
const usePortabilityStyles = makeStyles(() => ({
  portabilityGrid: (args: {isMobileView: boolean}) => ({
    minHeight: args.isMobileView ? 'auto' : '5.5rem',
  }),
}));

const Portability = () => {
  const {getQuoteFieldValue} = useQuoteField();

  const portabilityPaths = getPortabilityPaths();

  const isMobileView = !useMediaQuery((theme: any) => theme.breakpoints.up('sm'));

  const classes = usePortabilityStyles({isMobileView: isMobileView});

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'portability-row-1', className: classes.portabilityGrid},
      items: [
        {
          key: portabilityPaths.limitPortedCovDurationInd,
          element: (
            <QuoteFormElement
              elementType="FormControl"
              controlType="switch"
              label="Limited Ported Coverage Duration"
              checked={Boolean(getQuoteFieldValue(portabilityPaths.limitPortedCovDurationInd))}
              id={portabilityPaths.limitPortedCovDurationInd}
              onChange={NoOp}
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: portabilityPaths.covDuration,
          element: (
            <>
              {Boolean(getQuoteFieldValue(portabilityPaths.limitPortedCovDurationInd)) && (
                <QuoteFormElement
                  elementType="Text"
                  type="number"
                  label="Coverage Duration (Months)"
                  value={getQuoteFieldValue(portabilityPaths.covDuration)}
                  id={portabilityPaths.covDuration}
                  onChange={NoOp}
                  disabled
                  required
                />
              )}
            </>
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const AutomobileAccidentRider = () => {
  const {getBenefitLabelMapping} = useQuoteField();

  const benefit = 'automobileAccidentRiderDetails';
  const path = getBenefitLabelMapping(`${benefit}GHI3`, `${getParentStructurePath(0, 0)}.${benefit}`);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'automobile-accident-rider-row-1'},
      items: [
        {
          key: 'percent-of-plan-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="% of Plan Benefit Amount"
              id="percent-of-plan-benefit-amount"
              value={''}
            />
          ),
        },
      ],
    },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormRowGrid rows={rows} />
      </Grid>
      {Array.isArray([]) && (
        <Grid item xs={12}>
          <CheckboxInputGrid state={path} gridLength={4} />
        </Grid>
      )}
    </Grid>
  );
};

export const CriticalSupportConfinementRider = () => {
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'critical-support-row-1'},
      items: [
        {
          key: 'percent-of-plan-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="% of Plan Benefit Amount"
              value={''}
              id="percent-of-plan-benefit-amount"
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const GroupPolicyHolderFacilityRider = () => {
  const {getBenefitLabelMapping} = useQuoteField();

  const benefit = 'groupPolicyHolderDetails';
  const path = getBenefitLabelMapping(benefit, `${getParentStructurePath(0, 0)}.${benefit}`);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'Group-policy-holder-row-1'},
      items: [
        {
          key: 'percent-of-plan-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="% of Plan Benefit Amount"
              id="percent-of-plan-benefit-amount"
              value={''}
            />
          ),
        },
      ],
    },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormRowGrid rows={rows} />
      </Grid>
      {Array.isArray([]) && (
        <Grid item xs={12}>
          <CheckboxInputGrid state={path} gridLength={4} />
        </Grid>
      )}
    </Grid>
  );
};

export const OutpatientPhysicianTreatment = () => {
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'outpatient-physician-treatment-row-1'},
      items: [
        {
          key: 'type-of-rider',
          element: (
            <QuoteFormElement elementType="Select" label="Type of Rider" options={[]} id="type-of-rider" value={''} />
          ),
        },
        {
          key: 'max-number-of-days',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum number of days"
              type="number"
              id="max-number-of-days"
              value={''}
            />
          ),
        },
      ],
    },
    {
      rowProps: {key: 'outpatient-physician-treatment-row-2'},
      items: [
        {
          key: 'time-period',
          element: (
            <QuoteFormElement elementType="Select" label="Time Period" options={[]} id="time-period" value={''} />
          ),
        },
        {
          key: 'benefit-amount',
          element: (
            <QuoteFormElement elementType="Text" label="Benefit Amount" type="number" id="benefit-amount" value={0} />
          ),
        },
      ],
    },
  ];

  return (
    <Grid container>
      <Grid item xs={12}>
        <FormRowGrid rows={rows} />
      </Grid>
    </Grid>
  );
};

export const AdditionalOptionsandBenefitsGHI3Block = () => {
  const {getQuoteFieldValue} = useQuoteField();

  const portabilityPaths = getPortabilityPaths();
  const additionalOptionsandBenefits: ConditionalSection[] = [
    {
      Component: Portability,
      key: 'Portability',
      label: 'Portability',
      disabled: true,
      isApplied: getQuoteFieldValue(portabilityPaths.isPortabilityApplied),
    },
    {
      Component: AutomobileAccidentRider,
      key: 'AutomobileAccidentRider',
      label: 'Automobile Accident Rider',
      hidden: true,
      disabled: true,
      isApplied: false,
    },
    {
      Component: CriticalSupportConfinementRider,
      key: 'CriticalSupportConfinementRider',
      label: 'Critical Support Confinement Rider',
      hidden: true,
      disabled: true,
      isApplied: false,
    },
    {
      Component: HealthScreeningServices,
      key: 'HealthScreeningServices',
      label: 'Health Screening Services',
      hidden: true,
      disabled: true,
      isApplied: false,
    },
    {
      Component: GroupPolicyHolderFacilityRider,
      key: 'GroupPolicyHolderFacilityRider',
      label: 'Group Policy Holder Facility Rider',
      hidden: true,
      disabled: true,
      isApplied: false,
    },
    {
      Component: OutpatientPhysicianTreatment,
      key: 'OutpatientPhysicianTreatment',
      label: 'Outpatient Physician Treatment',
      hidden: true,
      disabled: true,
      isApplied: false,
    },
  ];

  return (
    <FormWrapper title="Additional Options and Benefits" collapsible withSeparator>
      <QuoteFormConditionalSection sections={additionalOptionsandBenefits} />
    </FormWrapper>
  );
};
/* End: Additional Options and Benefits */
